import { Button, SkeletonLine } from "@l30/ui";
import { Link } from "@tanstack/react-router";
import { ArrowUpRight, Upload } from "lucide-react";
import { useState } from "react";

import { usePortalPlan } from "../../api/plan.js";
import {
  usePortalBrief,
  usePortalInboxUpload,
  usePortalThreadMessages,
  usePortalThreads,
} from "../../api/portal-experience.js";
import {
  usePortalPublishedApprovals,
  usePortalPublishedBoardPacks,
  usePortalPublishedMeetings,
  usePortalPublishedSignatures,
} from "../../api/portal-published-objects.js";
import { usePortalDocuments } from "../../api/portal-vault.js";
import { usePortalMe } from "../../api/portal.js";
import { useSharedDocuments } from "../../api/shared-docs.js";
import { PortalQueryError } from "../../components/portal-query-error.js";
import { formatDate } from "../../lib/format-date.js";
import { EMPTY_PROGRAMME } from "../../lib/portal-copy.js";
import { buildAttentionItems, getNextMilestone } from "../../lib/portal-home-model.js";
import { tenantScopedPath } from "../../lib/portal-tenant-path.js";

import type { EngagementMilestone } from "@l30/shared";

/**
 * Transition33 Home — Figma lock 2026-08-10 "Q3 Programme Brief" plate.
 * Single briefing composition (not a metric dashboard). Wired to existing
 * portal brief / attention / vault / threads APIs.
 *
 * @see docs/design/references/2026-08-10-gop-portal-frames.md
 */

export function Transition33HomeBrief() {
  const meQuery = usePortalMe();
  const briefQuery = usePortalBrief();
  const planQuery = usePortalPlan();
  const documentsQuery = usePortalDocuments();
  const sharedDocsQuery = useSharedDocuments();
  const signaturesQuery = usePortalPublishedSignatures();
  const meetingsQuery = usePortalPublishedMeetings();
  const approvalsQuery = usePortalPublishedApprovals();
  const boardPacksQuery = usePortalPublishedBoardPacks();
  const threadsQuery = usePortalThreads();

  if (briefQuery.isError) {
    return (
      <PortalQueryError
        error={briefQuery.error}
        title="Couldn't load your workspace"
        retry={() => void briefQuery.refetch()}
      />
    );
  }

  const tenantSlug = meQuery.data?.tenantSlug ?? "";
  const scoped = (path: string) => (tenantSlug ? tenantScopedPath(tenantSlug, path) : path);

  const brief = briefQuery.data?.brief ?? null;
  const milestones = planQuery.data?.milestones ?? [];
  const nextMilestone = getNextMilestone(milestones);
  const attentionItems = buildAttentionItems({
    sharedDocuments: sharedDocsQuery.data?.documents ?? [],
    signatures: signaturesQuery.data?.signatures ?? [],
    approvals: approvalsQuery.data?.approvals ?? [],
  });

  const documentCount = documentsQuery.data?.total ?? 0;
  const reviewCount = sharedDocsQuery.data?.documents.length ?? 0;
  const activeWorkstreams = milestones.filter(
    (m) =>
      m.status === "upcoming" ||
      m.status === "on_track" ||
      m.status === "at_risk" ||
      m.status === "blocked",
  ).length;
  const workstreamCount =
    activeWorkstreams > 0 ? activeWorkstreams : Math.min(milestones.length, 4);

  const loading = briefQuery.isLoading || planQuery.isLoading;

  const actionRows = buildActionRows({
    attentionItems,
    nextMilestone,
    boardPack: boardPacksQuery.data?.boardPacks?.[0],
    meeting: meetingsQuery.data?.meetings?.[0],
    scoped,
  });

  const primaryThread = threadsQuery.data?.threads?.[0] ?? null;

  return (
    <article className="t33-plate relative overflow-hidden p-6 md:p-8">
      <div
        aria-hidden
        className="pointer-events-none absolute right-6 top-6 size-16 rounded-full border border-border opacity-40 md:right-8 md:top-8"
      >
        <div className="absolute inset-1/2 h-px w-full -translate-x-1/2 -translate-y-1/2 bg-border" />
        <div className="absolute inset-1/2 h-full w-px -translate-x-1/2 -translate-y-1/2 bg-border" />
      </div>

      <p className="font-mono text-[10px] uppercase tracking-[0.16em] text-fg-subtle">
        <span className="text-accent">Programme status briefing</span>
        <span className="mx-1.5 text-border-strong">·</span>
        Confidential — minister desk copy
      </p>

      {loading ? (
        <div className="mt-6 space-y-3">
          <SkeletonLine className="h-8 w-1/2" />
          <SkeletonLine />
          <SkeletonLine className="w-2/3" />
        </div>
      ) : !brief ? (
        <p className="mt-6 text-sm text-fg-muted">{EMPTY_PROGRAMME}</p>
      ) : (
        <>
          <h1 className="t33-display mt-3 max-w-3xl text-3xl md:text-4xl">{brief.title}</h1>
          <div className="mt-3 flex flex-wrap items-center gap-3 text-sm text-fg-subtle">
            <span>
              {brief.publishedAt
                ? `Published ${formatDate(brief.publishedAt)}`
                : `Updated ${formatDate(brief.updatedAt)}`}
            </span>
            {/* Engagement health is a staff-side assessment — never surfaced to the client. */}
          </div>

          <p className="t33-brief-body mt-6 max-w-3xl text-base leading-relaxed text-fg-muted md:text-[1.05rem]">
            {brief.summary}
          </p>
        </>
      )}

      <section className="mt-10">
        <h2 className="font-mono text-[10px] uppercase tracking-[0.16em] text-fg-subtle">
          Required actions &amp; milestones
        </h2>
        {actionRows.length === 0 ? (
          <p className="mt-4 text-sm text-fg-muted">No actions waiting — the programme is clear.</p>
        ) : (
          <ul className="mt-3 divide-y divide-border border-y border-border">
            {actionRows.map((row) => (
              <li key={row.id} className="flex flex-wrap items-start justify-between gap-3 py-4">
                <div className="flex min-w-0 gap-3">
                  <span className="mt-0.5 text-accent" aria-hidden>
                    →
                  </span>
                  <div className="min-w-0">
                    <div className="font-semibold text-fg">{row.title}</div>
                    {row.detail && <p className="mt-1 text-sm text-fg-muted">{row.detail}</p>}
                  </div>
                </div>
                <Link
                  to={row.href as "/t/$tenantSlug/portal"}
                  className="inline-flex shrink-0 items-center gap-1 text-sm font-medium text-fg-brand hover:underline"
                >
                  {row.cta}
                  <ArrowUpRight className="size-3.5" aria-hidden />
                </Link>
              </li>
            ))}
          </ul>
        )}
      </section>

      <section className="mt-10">
        <h2 className="font-mono text-[10px] uppercase tracking-[0.16em] text-fg-subtle">
          Programme signals
        </h2>
        <div className="mt-4 grid gap-6 sm:grid-cols-3">
          <SignalStat value={pad2(documentCount)} label="shared documents" />
          <SignalStat value={pad2(workstreamCount)} label="active workstreams" />
          <SignalStat value={pad2(reviewCount)} label="items for review" />
        </div>
      </section>

      <div className="mt-10 grid gap-8 border-t border-border pt-8 md:grid-cols-2">
        <CommunicationsBlock
          threadId={primaryThread?.id ?? null}
          threadTitle={primaryThread?.title ?? "Programme coordination"}
          threadsHref={scoped("/portal")}
        />
        <SubmissionsBlock />
      </div>

      <nav
        className="mt-10 border-t border-border pt-4 font-mono text-[10px] uppercase tracking-[0.12em] text-fg-subtle"
        aria-label="Documents index"
      >
        Documents index: <IndexLink href={scoped("/portal/calendar")}>Calendar</IndexLink>
        {" · "}
        <IndexLink href={scoped("/portal/board")}>Board</IndexLink>
        {" · "}
        <IndexLink href={scoped("/portal/working-groups")}>
          Committees &amp; working groups
        </IndexLink>
        {" · "}
        <IndexLink href={scoped("/portal/vault")}>Documents</IndexLink>
        {" · "}
        <IndexLink href={scoped("/portal/project-plan")}>Project plan</IndexLink>
      </nav>
    </article>
  );
}

function SignalStat({ value, label }: { value: string; label: string }) {
  return (
    <div>
      <div className="t33-display text-4xl tabular-nums text-fg">{value}</div>
      <div className="mt-1 text-sm text-fg-muted">{label}</div>
    </div>
  );
}

function CommunicationsBlock({
  threadId,
  threadTitle,
  threadsHref,
}: {
  threadId: string | null;
  threadTitle: string;
  threadsHref: string;
}) {
  const messagesQuery = usePortalThreadMessages(threadId);
  const latest = messagesQuery.data?.messages?.[0];

  return (
    <section>
      <h2 className="font-mono text-[10px] uppercase tracking-[0.16em] text-fg-subtle">
        Communications
      </h2>
      <p className="mt-3 font-semibold text-fg">{threadTitle}</p>
      {latest ? (
        <p className="mt-2 line-clamp-3 text-sm text-fg-muted">
          <span className="font-medium text-fg">{latest.authorName}</span>
          {latest.createdAt ? ` (${formatDate(latest.createdAt)})` : ""}
          {" — "}
          {latest.body}
        </p>
      ) : (
        <p className="mt-2 text-sm text-fg-muted">
          Programme coordination threads appear here when Latitude30 opens them.
        </p>
      )}
      <Link
        to={threadsHref as "/t/$tenantSlug/portal"}
        className="mt-3 inline-block text-sm font-medium text-fg-brand hover:underline"
      >
        View thread →
      </Link>
    </section>
  );
}

function SubmissionsBlock() {
  const upload = usePortalInboxUpload();
  const [file, setFile] = useState<File | null>(null);

  function handleChange(e: React.ChangeEvent<HTMLInputElement>) {
    setFile(e.target.files?.[0] ?? null);
  }

  function handleUpload() {
    if (!file) return;
    upload.mutate(
      { file },
      {
        onSuccess: () => setFile(null),
      },
    );
  }

  return (
    <section>
      <h2 className="font-mono text-[10px] uppercase tracking-[0.16em] text-fg-subtle">
        Submissions
      </h2>
      <p className="mt-3 font-semibold text-fg">Upload to inbox</p>
      <p className="mt-2 text-sm text-fg-muted">
        Files go to the portal inbox before staff accept them into the depository.
      </p>
      <div className="mt-3 flex flex-wrap items-center gap-2">
        <label className="inline-flex cursor-pointer items-center gap-2 text-sm font-medium text-fg-brand hover:underline">
          <Upload className="size-4" aria-hidden />
          {file ? file.name : "Upload file"}
          <input type="file" className="sr-only" onChange={handleChange} />
        </label>
        {file && (
          <Button size="sm" onClick={handleUpload} disabled={upload.isPending}>
            {upload.isPending ? "Uploading…" : "Send"}
          </Button>
        )}
      </div>
      {upload.isSuccess && <p className="mt-2 text-sm text-success-fg">Upload received.</p>}
      {upload.isError && (
        <p className="mt-2 text-sm text-danger-solid">
          {upload.error instanceof Error ? upload.error.message : "Upload failed."}
        </p>
      )}
    </section>
  );
}

function IndexLink({ href, children }: { href: string; children: string }) {
  return (
    <Link to={href as "/t/$tenantSlug/portal"} className="hover:text-fg-brand hover:underline">
      {children}
    </Link>
  );
}

function buildActionRows({
  attentionItems,
  nextMilestone,
  boardPack,
  meeting,
  scoped,
}: {
  attentionItems: ReturnType<typeof buildAttentionItems>;
  nextMilestone: EngagementMilestone | undefined;
  boardPack: { title: string; meetingDate: string } | undefined;
  meeting: { title: string; recordedAt: string } | undefined;
  scoped: (path: string) => string;
}): { id: string; title: string; detail?: string; href: string; cta: string }[] {
  const rows: { id: string; title: string; detail?: string; href: string; cta: string }[] = [];

  for (const item of attentionItems.slice(0, 2)) {
    rows.push({
      id: item.id,
      title: item.title,
      detail: item.meta,
      href: item.kind === "review" ? scoped("/portal/reviews") : scoped("/portal/published"),
      cta: item.kind === "review" ? "For review" : "Published",
    });
  }

  if (boardPack && rows.length < 3) {
    rows.push({
      id: `pack-${boardPack.title}`,
      title: boardPack.meetingDate
        ? `Board meeting ${formatDate(boardPack.meetingDate)}`
        : boardPack.title,
      detail: "Ready for you to read and confirm",
      href: scoped("/portal/board"),
      cta: "Go to Board",
    });
  } else if (meeting && rows.length < 3) {
    rows.push({
      id: `meeting-${meeting.title}`,
      title: meeting.title,
      detail: meeting.recordedAt ? formatDate(meeting.recordedAt) : undefined,
      href: scoped("/portal/board"),
      cta: "Go to Board",
    });
  }

  if (nextMilestone && rows.length < 3) {
    rows.push({
      id: `ms-${nextMilestone.id}`,
      title: nextMilestone.targetDate
        ? `${nextMilestone.title} ${formatDate(nextMilestone.targetDate)}`
        : nextMilestone.title,
      detail: nextMilestone.description ?? "Programme milestone",
      href: scoped("/portal/project-plan"),
      cta: "Project plan",
    });
  }

  return rows.slice(0, 3);
}

function pad2(n: number): string {
  return n < 10 ? `0${n}` : String(n);
}
